import React, { useState, useRef, useCallback, useEffect } from "react";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

const mapContainerStyle = {
  width: "100%",
  height: "500px",
};

const defaultCenter = { lat: 51.505, lng: -0.09 };
const libraries = ["places"];

const buildLocationDetails = (components = []) => {
  let city = "";
  let state = "";
  let postalCode = "";
  let country = "";

  components.forEach((component) => {
    if (component.types.includes("locality")) {
      city = component.long_name;
    }
    if (component.types.includes("administrative_area_level_1")) {
      state = component.long_name;
    }
    if (component.types.includes("postal_code")) {
      postalCode = component.long_name;
    }
    if (component.types.includes("country")) {
      country = component.long_name;
    }
  });

  return { city, state, postalCode, country };
};

const LocationPicker = ({ onConfirm }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [address, setAddress] = useState("");

  const mapRef = useRef();
  const searchBoxRef = useRef();
  const closeModelRef = useRef();

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries,
  });

  // when modal opens → try current location
  useEffect(() => {
    const modalEl = document.getElementById("locationModal");
    if (!modalEl) return;

    modalEl.addEventListener("shown.bs.modal", () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const currentPos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setMapCenter(currentPos);
            reverseGeocode(currentPos);
          },
          () => {
            console.warn("Geolocation denied, using default center.");
          }
        );
      }
    });
  }, []);

// reverse geocode helper
const reverseGeocode = (latlng) => {
  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ location: latlng }, (results, status) => {
    if (status === "OK" && results[0]) {
      const locationDetails = buildLocationDetails(results[0].address_components);
      const fullAddress = results[0].formatted_address;

      setAddress(fullAddress);
      setSelectedLocation((prev) => ({
        ...latlng,
        address: fullAddress,
        ...locationDetails,
      }));
    } else {
      setAddress("Address not found");
    }
  });
};



  // map load handler + search box
  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;

    const input = document.getElementById("pac-input");
    if (!input) return;

    const searchBox = new window.google.maps.places.SearchBox(input);
    searchBoxRef.current = searchBox;

    map.addListener("bounds_changed", () => {
      searchBox.setBounds(map.getBounds());
    });

    searchBox.addListener("places_changed", () => {
      const places = searchBox.getPlaces();
      if (!places || places.length === 0) return;

      const place = places[0];
      if (!place.geometry) return;

      const newPos = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      const fullAddress = place.formatted_address || place.name || "";
      const locationDetails = buildLocationDetails(place.address_components);

      setSelectedLocation({
        ...newPos,
        address: fullAddress,
        ...locationDetails,
      });
      setAddress(fullAddress);
      setMapCenter(newPos);
      map.panTo(newPos);
      map.setZoom(15);
    });
  }, []);

  // click on map → set location
  const handleMapClick = (e) => {
    console.log(e)
    const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setSelectedLocation(pos);
    reverseGeocode(pos);
  };

  // confirm location → send to parent + close modal
  const handleConfirm = () => {
   
    if (onConfirm) {
      onConfirm(selectedLocation, address); // 🔹 keep parent callback
    }
    closeModelRef.current.click()

  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <>
      {/* Trigger button */}
      <button type="button" className="fild-icon" data-bs-toggle="modal" data-bs-target="#locationModal">
        <i className="fi fi-rs-location-alt"></i>
      </button>

      {/* Bootstrap Modal */}
      <div className="modal fade" id="locationModal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content location-modal-content">
            <div className="d-flex justify-content-between align-items-center px-3 py-2">
              <h4 className="mb-0">Select Location</h4>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={closeModelRef}></button>
            </div>

            <div className="modal-body p-0">
              <div style={{ width: "100%", height: "500px", position: "relative" }}>
                {/* Search Box */}
                <input
                  id="pac-input"
                  type="text"
                  placeholder="Search location..."
                  style={{
                    position: "absolute",
                    zIndex: 10,
                    top: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "300px",
                    padding: "8px 12px",
                    borderRadius: "25px",
                    border: "1px solid #ccc",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                  }}
                />

                {/* Google Map */}
                <GoogleMap mapContainerStyle={mapContainerStyle} zoom={13} center={mapCenter} onLoad={handleMapLoad} onClick={handleMapClick}>
                  {selectedLocation && <Marker position={selectedLocation} />}
                </GoogleMap>

                {/* Confirm Button */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "15px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 10,
                  }}
                >
                  <button className="btn btn-primary" onClick={handleConfirm}  type="button" >
                    Confirm Location
                  </button>
                </div>
              </div>

              {/* optional: show selected address */}
              {/* {address && (
                <p className="text-center mt-2">
                  <strong>Selected Address:</strong> {address}
                </p>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LocationPicker;
